import { makeAutoObservable, runInAction, action } from 'mobx';
import groupBy from 'lodash/groupBy';
import dayjs, { Dayjs } from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import isObject from 'lodash/isObject';
import { validate } from 'uuid';
import { FieldState } from 'formstate';
import CartDeliveryState from '@features/cart/mobx/state/CartDeliveryState';
import CartProductState from '@features/cart/mobx/state/CartProductState';
import NextCheckoutApi from '@features/api/nextApiService/nextCheckoutApi';
import NextDeliveryApi from '@features/api/nextApiService/nextDeliveryApi';
import logger from '@utils/logger';
import { formatPrice } from '@utils/formatPrice';
import { CartTotalType, CartTotalValueType, CartTaxGroup, CartTaxesTotalGroup, ICartAvailableDelivery, ICartItem, TPositionType, AvailabilityType, ICoupon, IDiscountData, IDelivery, ICartService, IAdditionalArgument, ICartData, ITransportsCartAvailableDelivery } from '@features/cart/typescript';
import { getRegion } from '@utils/misc';
import { IAvailableServiceV2, IOrderDetails, IBasicAddress } from '@features/checkout/typescript';
import { ASSEMBLY_SERVICE_VAT_8_ID } from '@features/cart/const';
import { calculateAssemblyProductsPriceFromServices, calculateMixedAssemblyProductsPriceFromCart, calculateMixedAssemblyDisplayPrice, calculateMixedAssemblySavings, calculateMixedEligibleProductsPriceComparison, getBringServiceGrossPriceFromPool, calculateMixedVatAssemblyBreakdown, calculateMixedVatProductsBreakdown, calculateSelfAssemblyProductsPrice, CartProductPriceInput, getMixedVatBreakdownFromTaxesTotals, isMixedAssemblyTaxRate, MixedVatAssemblyBreakdown, MixedVatProductsBreakdown } from '@features/checkout/utils/deliveryV2Adapter';
import { IApiError, TRegion } from '@typescript/index';
import ReaffiliatesViewMiniCart from '@reaffiliates/ReaffiliatesViewMiniCart';
import { TOrderStatusType, TOrderTransport, ICart, TCartStatusType } from '@features/userPanel/typescript';
import { ASSEMBLY_SERVICE_NAME, BRINGING_SERVICE_NAME, EXPRESS_SERVICE_NAME, CLOSET_SUBTYPE_ID, CLOSET_SUBTYPE_MASKING_ID, CLOSET_TYPE_ID, PROMO_CONSENT_COUPON_CODE, STOR_CATEGORY_ID } from '@features/cart/const';
import { clearSelectedDeliveryAdditionalData } from '@utils/deliveryAdditionalDataHelper';
import browserStorage from '@utils/browserStorage';
import { CANNOT_MODIFY_CART } from '@features/api/platformApiService/const';
import { AVAILABLE_REGIONS } from '@const/index';
import { IDeliveryDataResponse } from '@features/api/platformApiService/typescript';
dayjs.extend(minMax);
export type SideBarType = 'assembly' | 'delivery' | 'instalmentPayment';
export type StorAssemblyState = 'none' | 'pure' | 'mixed';
export default class CartStore {
  public cartId = '';
  public cartStatus: TOrderStatusType | TCartStatusType = '' as TOrderStatusType;
  public language = '';
  public customerId = '';
  public deliveryAddressId = '';
  public invoiceAddressId = '';
  public products: Map<string, CartProductState> = new Map();
  public deliveries: Map<string, CartDeliveryState> = new Map();
  public availableDeliveries: Map<string, Array<ICartAvailableDelivery>> = new Map();
  public availableServicesPool: Record<string, IAvailableServiceV2[]> = {};
  public deliveryDataConfig: Record<string, IDeliveryDataResponse> = {};
  public services: ICartService[] = [];
  public selectedBringingService: Record<string, ICartService> = {};
  public selectedAssemblyService: Record<string, ICartService> = {};
  public selectedExpressService: Record<string, ICartService> = {};
  public selectedDeliveries: Record<string, IDelivery> = {};
  public transports: Array<TOrderTransport> = [] as Array<TOrderTransport>;
  public totals: Record<CartTotalType, CartTotalValueType> = {} as Record<CartTotalType, CartTotalValueType>;
  public taxesTotals: CartTaxesTotalGroup[] = [];
  public taxes: CartTaxGroup[] = [];
  public isSideBarOpened = false;
  public isFetchingInProgress = false;
  public showProductsAvailableWithDelayMessage = false;
  public showProductsUnavailableMessage = false;
  public showProductMaxQuantityMessage = false;
  public maxQuantityBlockedWithoutProduct = false;
  public showStorAssemblyPureMessage = false;
  public showStorAssemblyMixedMessage = false;
  public showNewTaxRateMessage = false;
  public currentAssemblyTaxRate = '23';
  private confirmedUnavailableQuantityProducts = new Set<string>();
  private dismissedMaxQuantityPositions = new Set<string>();
  public discountInputOpened = false;
  public coupon: ICoupon | null = null;
  public discountData: IDiscountData = {} as IDiscountData;
  public discountCode: FieldState<string> = new FieldState('');
  public errorDiscountValidate = false;
  public discountCouponActive = false;
  public spinnerLoader = false;
  public isCartOutdated = false;
  public showChangedDeliveryTimeMessage = false;
  public deliveryDays: number[] | null = null;
  public deliveryAddres: IBasicAddress | null = null;
  public note: string = '';
  public cuponReachedMaxUsage = false;
  private languageSyncPromise: Promise<ICart | null> | null = null;
  constructor() {
    makeAutoObservable(this);
  }
  public loadExternalCart = async (cartId: string) => {
    this.getCartFromLocalStorage();
    const oldCartId = this.cartId;
    this.clearStore();
    this.cartId = cartId;
    const {
      cartId: resultCartId
    } = await this.fetchAndPopulateCartStore();
    if (resultCartId === '') {
      this.cartId = oldCartId;
      await this.fetchAndPopulateCartStore();
    }
    this.saveCartInLocalStorage();
    return resultCartId;
  };
  public fetchAndPopulateCartStore = async (withLoader = true, additionalArgument?: IAdditionalArgument) => {
    runInAction(() => {
      if (withLoader) this.isFetchingInProgress = true;
    });
    const data = await this.handleCartStatus();
    const isOfCartType = (object: ICart | IApiError): object is ICart => 'cartId' in object;
    runInAction(() => {
      this.resetStore();
      if (isOfCartType(data)) this.populateStoreWithFetchedCartData(data);
      if (this.unavailableProductsQuantityToConfirm.length > 0) {
        this.showProductMaxQuantityMessage = true;
      } else if (!this.maxQuantityBlockedWithoutProduct) {
        this.showProductMaxQuantityMessage = false;
      }
      if (withLoader) this.isFetchingInProgress = false;
    });
    if (Object.keys(this.products).length > 0) {
      void this.fetchAvailableDeliveries(additionalArgument);
    }
    this.checkStorAssembly();
    return {
      cartId: this.cartId,
      products: this.products
    };
  };
  public invalidateCart = async (isCartOrdered = false) => {
    this.cartId = '';
    this.language = '';
    this.saveCartInLocalStorage();
    clearSelectedDeliveryAdditionalData();
    this.products.clear();
    this.deliveries.clear();
    this.services.length = 0;
    this.transports.length = 0;
    this.totals = {} as Record<CartTotalType, CartTotalValueType>;
    this.taxesTotals = [];
    this.taxes = [];
    if (!isCartOrdered) {
      void NextCheckoutApi.clearOrder();
    }
  };
  private handleCartStatus = async () => {
    let data = {} as ICart | IApiError;
    let isCartCanceled = false;
    let isCartOrdered = false;
    let cartDoesNotExists = false;
    let badCartId = false;
    const isCorrectCartId = this.cartId && this.cartId !== 'undefined' && validate(this.cartId);
    if (isCorrectCartId) {
      data = await NextCheckoutApi.getCart(this.cartId);
      const {
        status
      } = data;
      isCartCanceled = status === 'canceled';
      isCartOrdered = status === 'ordered';
      cartDoesNotExists = status === 404;
      badCartId = status === 422;
    }
    const isCartInvalid = !isCorrectCartId || isCartOrdered || isCartCanceled || cartDoesNotExists || badCartId;
    if (isCartInvalid) {
      void this.invalidateCart(isCartOrdered);
      return {} as ICart;
    }
    if (this.isCartData(data)) {
      return this.syncCartLanguageIfNeeded(data);
    }
    return data;
  };
  public ensureCartLanguageMatchesLocale = async () => {
    if (!this.cartId || this.cartId === 'undefined' || !validate(this.cartId)) return;
    if (this.cartStatus && this.cartStatus !== TCartStatusType.draft) return;
    const currentLanguage = NextCheckoutApi.getCartLanguage();
    if (!currentLanguage || (this.language || '').toLowerCase() === currentLanguage) return;
    const synced = await this.syncCartLanguageIfNeeded({
      cartId: this.cartId,
      status: TCartStatusType.draft,
      language: this.language || undefined,
      positions: [],
      totals: []
    });
    if (!this.isCartData(synced)) return;
    if (Array.isArray(synced.positions) && synced.positions.length > 0) {
      runInAction(() => {
        const {
          cartId
        } = this;
        this.resetStore();
        this.cartId = cartId;
        this.populateStoreWithFetchedCartData(synced);
      });
      return;
    }
    if (synced.language && synced.language.toLowerCase() !== this.language.toLowerCase()) {
      runInAction(() => {
        this.language = synced.language || currentLanguage;
      });
    }
  };
  private isCartData = (object: unknown): object is ICart => {
    if (!object || typeof object !== 'object') return false;
    if (!('cartId' in object)) return false;
    if ('error' in object && Boolean((object as {
      error?: unknown;
    }).error)) return false;
    return true;
  };
  private syncCartLanguageIfNeeded = async (cart: ICart): Promise<ICart> => {
    const currentLanguage = NextCheckoutApi.getCartLanguage();
    if (!currentLanguage || !cart.cartId) return cart;
    const isDraft = !cart.status || cart.status === TCartStatusType.draft;
    if (!isDraft) return cart;
    if ((cart.language || '').toLowerCase() === currentLanguage) return cart;
    if (this.languageSyncPromise) {
      const inFlight = await this.languageSyncPromise;
      return inFlight && this.isCartData(inFlight) ? inFlight : cart;
    }
    this.languageSyncPromise = this.requestCartLanguageChange(cart, currentLanguage);
    try {
      const result = await this.languageSyncPromise;
      return result && this.isCartData(result) ? result : cart;
    } finally {
      this.languageSyncPromise = null;
    }
  };
  private requestCartLanguageChange = async (cart: ICart, language: string): Promise<ICart | null> => {
    try {
      const result = await NextCheckoutApi.changeCartLanguage(cart.cartId, language);
      if (this.isCartData(result)) {
        return result;
      }
      if (result && typeof result === 'object' && 'success' in result && result.success) {
        const refetched = await NextCheckoutApi.getCart(cart.cartId);
        if (this.isCartData(refetched)) {
          return refetched;
        }
        return {
          ...cart,
          language
        };
      }
      logger.error(result);
      return cart;
    } catch (error) {
      logger.error(error);
      return cart;
    }
  };
  public fetchAvailableDeliveries = async (additionalArgument?: IAdditionalArgument): Promise<boolean> => {
    runInAction(() => {
      this.isFetchingInProgress = true;
    });
    let dataFetchedSuccessfully = false;
    const fetchedDeliveries: Map<string, ICartAvailableDelivery[]> = new Map();
    let additional = additionalArgument;
    const region = getRegion('uppercase') as TRegion;
    if (!additionalArgument && this.deliveryAddres && [AVAILABLE_REGIONS.poland, AVAILABLE_REGIONS.euro].includes(region)) {
      additional = {
        key: 'postalCode',
        value: this.deliveryAddres.postalCode || ''
      };
    }
    if (!additionalArgument && this.deliveryAddres?.country && region === AVAILABLE_REGIONS.euro) {
      additional = {
        key: 'countryCode',
        value: this.deliveryAddres?.country
      };
    }
    await Promise.allSettled(this.transports.map(async transport => {
      const deliveriesResponse = await NextCheckoutApi.getAvailableDeliveries(this.cartId, transport.transportGroupId, additional);
      const {
        success,
        data,
        servicesPool
      } = deliveriesResponse;
      if (success) {
        dataFetchedSuccessfully = true;
        runInAction(() => {
          if (Array.isArray(servicesPool) && servicesPool.length) {
            this.availableServicesPool[transport.transportGroupId] = servicesPool;
          }
          if (Array.isArray(data) && data.length) {
            fetchedDeliveries.set(transport.transportGroupId, data);
          }
          const deliveries = fetchedDeliveries.get(transport.transportGroupId);
          if (deliveries) {
            deliveries.forEach(delivery => {
              if (additional?.key === 'postalCode' && additional?.value) {
                delivery.postalCode = additional.value || '';
              } else {
                delivery.postalCode = '';
              }
            });
          }
        });
      } else {
        runInAction(() => {
          const deliveries = fetchedDeliveries.get(transport.transportGroupId);
          if (deliveries) {
            deliveries.forEach(delivery => {
              delivery.price = null;
            });
          }
        });
      }
    }));
    runInAction(() => {
      if (dataFetchedSuccessfully) {
        this.availableDeliveries.clear();
        fetchedDeliveries.forEach((value, key) => {
          this.availableDeliveries.set(key, value);
        });
      }
      this.handleDeliveryTime();
      this.isFetchingInProgress = false;
    });
    return true;
  };
  public createCart = async () => {
    const currentCartId = this.getCartFromLocalStorage();
    if (currentCartId) {
      const data = await this.handleCartStatus();
      const isOfCartType = (object: ICart | IApiError): object is ICart => 'cartId' in object;
      if (isOfCartType(data)) return this.cartId;
    }
    try {
      await NextCheckoutApi.clearOrder();
      clearSelectedDeliveryAdditionalData();
      const cart = await NextCheckoutApi.createCart();
      if ('error' in cart && cart.error) {
        throw new Error('Failed to create cart');
      }
      const {
        cartId
      } = cart;
      if (!cartId || cartId === 'undefined' || !validate(cartId)) {
        throw new Error('Failed to create cart: invalid cartId');
      }
      runInAction(() => {
        this.cartId = cartId;
        this.language = NextCheckoutApi.getCartLanguage();
        this.saveCartInLocalStorage();
      });
      return this.cartId;
    } catch (error) {
      logger.error(error);
      throw error instanceof Error ? error : new Error(String(error));
    }
  };
  private resetStore() {
    this.cartId = '';
    this.customerId = '';
    this.deliveryAddressId = '';
    this.invoiceAddressId = '';
    this.language = '';
    this.products = new Map();
    this.deliveries = new Map();
    this.availableServicesPool = {};
    this.transports = [];
    this.services = [];
    this.totals = {} as Record<CartTotalType, CartTotalValueType>;
    this.taxesTotals = [];
    this.taxes = [];
    this.coupon = null;
    this.showProductsAvailableWithDelayMessage = false;
    this.showProductsUnavailableMessage = false;
  }
  private productsFactory(products: Array<ICartItem>) {
    if (!products.length) return;
    products.forEach(product => this.products.set(product.positionId, new CartProductState(this.fetchAndPopulateCartStore, this.cartId, product, this, this.confirmedUnavailableQuantityProducts.has(product.positionId))));
  }
  private servicesFactory(services: Array<ICartService>) {
    if (!services.length) return;
    this.services = services;
  }
  private deliveriesFactory(deliveries: Array<IDelivery>) {
    if (!deliveries.length) return;
    const newDeliveries = new Map<string, CartDeliveryState>();
    deliveries.forEach(delivery => {
      newDeliveries.set(delivery.positionId, new CartDeliveryState(delivery));
    });
    this.deliveries = newDeliveries;
  }
  private checkSelectedDeliveries() {
    const newSelectedDeliveries = new Map<string, IDelivery>();
    this.transports.forEach(transport => {
      const {
        positions,
        transportGroupId
      } = transport;
      positions.forEach(position => {
        const delivery = this.deliveries.get(position);
        if (delivery) {
          newSelectedDeliveries.set(transportGroupId, delivery);
        }
      });
    });
    this.selectedDeliveries = Object.fromEntries(newSelectedDeliveries);
  }
  private checkSelectedServices() {
    const newSelectedBringingService = new Map<string, ICartService>();
    const newSelectedAssemblyService = new Map<string, ICartService>();
    const newSelectedExpressService = new Map<string, ICartService>();
    this.transports.forEach(transport => {
      const {
        positions,
        transportGroupId
      } = transport;
      positions.forEach(position => {
        const service = this.services.find(s => s.positionId === position);
        if (service) {
          if (service.name === BRINGING_SERVICE_NAME) {
            newSelectedBringingService.set(transportGroupId, service);
          }
          if (service.name === ASSEMBLY_SERVICE_NAME) {
            newSelectedAssemblyService.set(transportGroupId, service);
          }
          if (service.name === EXPRESS_SERVICE_NAME) {
            newSelectedExpressService.set(transportGroupId, service);
          }
        }
      });
    });
    this.selectedBringingService = Object.fromEntries(newSelectedBringingService);
    this.selectedAssemblyService = Object.fromEntries(newSelectedAssemblyService);
    this.selectedExpressService = Object.fromEntries(newSelectedExpressService);
  }
  private positionsFactoryDispatcher(type: TPositionType, grouppedByType) {
    if (!('service' in grouppedByType)) {
      this.services = [];
    }
    if (!('delivery' in grouppedByType)) {
      this.deliveries = new Map();
    }
    if (!('product' in grouppedByType)) {
      this.products = new Map();
    }
    switch (type) {
      case 'product':
        this.productsFactory(grouppedByType[type]);
        break;
      case 'delivery':
        this.deliveriesFactory(grouppedByType[type]);
        break;
      case 'service':
        this.servicesFactory(grouppedByType[type]);
        break;
      default:
        break;
    }
  }
  public populateStoreWithFetchedCartData = (cartData: ICart | IOrderDetails) => {
    this.cartStatus = cartData.status;
    this.selectedDeliveries = {};
    this.selectedAssemblyService = {};
    this.selectedBringingService = {};
    this.selectedExpressService = {};
    this.taxesTotals = Array.isArray(cartData.taxesTotals) ? cartData.taxesTotals : [];
    this.taxes = Array.isArray(cartData.taxes) ? cartData.taxes : [];
    Object.keys(cartData).forEach(key => {
      if (!cartData[key]) return;
      if (typeof cartData[key] === 'string') this[key] = cartData[key];
      if (key === 'coupon') {
        this.coupon = isObject(cartData[key]) ? cartData[key] as ICoupon : null;
      }
      if (!cartData[key].length) return;
      if (key === 'positions') {
        const grouppedByType = groupBy(cartData[key], pos => pos.type);
        Object.keys(grouppedByType).forEach(posTypeKey => {
          this.positionsFactoryDispatcher(posTypeKey as TPositionType, grouppedByType);
        });
      }
      if (key === 'totals') {
        cartData[key].forEach(el => {
          this.totals[el.totalType] = el.total;
        });
        return;
      }
      if (key === 'transports') {
        cartData[key].forEach(el => {
          this.transports.push(el);
        });
      }
    });
    this.checkSelectedDeliveries();
    this.checkSelectedServices();
    this.checkStorAssembly();
    this.handleDeliveryTime();

    // Fetch delivery data configuration after cart is populated
    void this.fetchDeliveryDataConfig();
  };
  public fetchDeliveryDataConfig = async () => {
    if (this.deliveries.size > 0) {
      const deliveryConfigData: Record<string, IDeliveryDataResponse> = {};
      const deliveryPromises = Array.from(this.deliveries.entries()).map(async ([, delivery]) => {
        try {
          const deliveryData = await NextDeliveryApi.getDeliveryData(delivery.deliveryTypeId);
          deliveryConfigData[delivery.deliveryTypeId] = deliveryData;
        } catch (error) {
          logger.error(`Failed to fetch delivery data for ${delivery.deliveryTypeId}:`, error);
          deliveryConfigData[delivery.deliveryTypeId] = {
            error: true,
            message: 'Failed to fetch delivery data',
            deliveryTypeNameLanguageKey: '',
            deliveryType: '',
            isBringingAvailable: false,
            isAssembleAvailable: false,
            isVatAvailable: false,
            isTaxAvailable: false,
            isPaidOnDelivery: false,
            personalPickupStoreType: null,
            leadTime: 0,
            logisticsTime: 0,
            provider: '',
            availabilityStatus: '',
            availabilityDate: '',
            deliveryDate: '',
            pricingWithRequiredServices: [],
            configuration: {
              additionalFields: {
                comment: {
                  exist: false,
                  required: false
                },
                customPrice: {
                  exist: false,
                  required: false
                },
                parcelLocker: {
                  exist: false,
                  required: false
                },
                pickupPointId: {
                  exist: false,
                  required: false
                }
              },
              requirements: {
                retailCustomer: false,
                assemblyService: false,
                bringService: false
              }
            }
          };
        }
      });
      await Promise.all(deliveryPromises);
      runInAction(() => {
        this.deliveryDataConfig = deliveryConfigData;
      });
    }
  };
  public getLatestDeliveryForPosition = (positionId: string): number => {
    let businessDaysLeadTime = 10;
    this.transports.forEach(transport => {
      const transportHasPosition = transport.positions.includes(positionId);
      if (transportHasPosition) {
        const {
          transportGroupId
        } = transport;
        const deliveries = this.availableDeliveries.get(transportGroupId);
        if (deliveries) {
          const transportBusinessDaysLeadTime = Math.min(...deliveries.map((d: ICartAvailableDelivery) => d.logisticsTime + d.leadTime));
          if (transportBusinessDaysLeadTime) businessDaysLeadTime = transportBusinessDaysLeadTime;
        }
      }
    });
    return businessDaysLeadTime;
  };
  public getLatestAvailabilityDatePerTransportGroup = (): Record<string, Dayjs> => {
    const availabilityDatesRecord: Record<string, Dayjs> = {};
    this.transports.forEach(transport => {
      const {
        transportGroupId,
        positions
      } = transport;
      const products = Array.from(this.products.values()).filter(product => positions.includes(product.position));
      const availabilityDates: Dayjs[] = [];
      products.forEach(product => {
        const {
          status,
          dateFrom
        } = product.availability;
        const isAvailable = status === AvailabilityType.available;
        const parsedDate = dateFrom.replace('UTC', '');
        if (isAvailable && dayjs(parsedDate).isValid()) {
          const date = dayjs(parsedDate).isValid() ? dayjs(parsedDate) : dayjs(new Date());
          const onlyFutureDate = dayjs.max(date, dayjs(new Date()));
          if (onlyFutureDate) {
            availabilityDates.push(onlyFutureDate);
          }
        }
      });
      if (availabilityDates.length === 0) {
        const product = products[0];
        const {
          dateFrom
        } = product.availability;
        const parsedDate = dateFrom.replace('UTC', '');
        if (dayjs(parsedDate).isValid()) {
          const date = dayjs(parsedDate).isValid() ? dayjs(parsedDate) : dayjs(new Date());
          const onlyFutureDate = dayjs.max(date, dayjs(new Date()));
          if (onlyFutureDate) {
            availabilityDates.push(onlyFutureDate);
          }
        }
      }
      const latestAvailabilityDate = dayjs.max(availabilityDates);
      if (latestAvailabilityDate) {
        availabilityDatesRecord[transportGroupId] = latestAvailabilityDate;
      }
    });
    return availabilityDatesRecord;
  };
  public get selectedAvailableDeliveries(): ITransportsCartAvailableDelivery {
    const selectedDeliveries: ITransportsCartAvailableDelivery = {};
    if (!this.availableDeliveries.size) {
      return selectedDeliveries;
    }
    this.transports.forEach(transport => {
      const {
        transportGroupId
      } = transport;
      const selectedDelivery = this.selectedDeliveries[transportGroupId];
      if (!selectedDelivery) {
        return;
      }
      const matchedDelivery = this.availableDeliveries.get(transportGroupId)?.find(delivery => delivery.deliveryTypeId === selectedDelivery.deliveryTypeId);
      if (matchedDelivery) {
        selectedDeliveries[transportGroupId] = matchedDelivery;
      }
    });
    return selectedDeliveries;
  }
  public get estimatedDelivery(): Record<string, {
    days: number;
    date: string;
  }> {
    if (!this.availableDeliveries.size) return {};
    const estimatedDelivery: Record<string, {
      days: number;
      date: string;
    }> = {};
    const currentDayTime = dayjs(new Date().setHours(0, 0, 0, 0));
    this.transports.forEach(transport => {
      const {
        transportGroupId
      } = transport;
      const selectedDelivery = this.availableDeliveries.get(transportGroupId)?.find(d => d.deliveryTypeId === this.selectedDeliveries[transportGroupId]?.deliveryTypeId);
      if (selectedDelivery) {
        const {
          expectedDeliveryDate
        } = selectedDelivery;

        // W przypadku wybranej dostawy leadTime już zawiera dni robocze
        const deliveryDate = dayjs(expectedDeliveryDate);
        const daysFromNowToDelivery = dayjs(deliveryDate).diff(currentDayTime, 'd');
        estimatedDelivery[transportGroupId] = {
          days: daysFromNowToDelivery,
          date: deliveryDate.format('DD.MM.YYYY')
        };
      } else {
        const deliveries = this.availableDeliveries.get(transportGroupId);
        if (deliveries) {
          const earliestDeliveryDate = deliveries.map((d: ICartAvailableDelivery) => dayjs(d.expectedDeliveryDate)).reduce((earliest, current) => current.isBefore(earliest) ? current : earliest);
          const daysFromNowToDelivery = dayjs(earliestDeliveryDate).diff(currentDayTime, 'd');
          estimatedDelivery[transportGroupId] = {
            days: daysFromNowToDelivery,
            date: earliestDeliveryDate.format('DD.MM.YYYY')
          };
        }
      }
    });
    if (Object.keys(estimatedDelivery).length <= 0) return {};
    return estimatedDelivery;
  }
  public get totalQuantity() {
    const quantities: Array<number> = [0];
    this.products.forEach(product => {
      quantities.push(product.quantity);
    });
    return quantities.reduce((a, b) => a + b);
  }
  public getProductQuantityById = (productId: string) => {
    let quantity = 0;
    this.products.forEach(product => {
      if (product.productId.toString() === productId) {
        quantity = product.quantity;
        return;
      }
    });
    return quantity;
  };
  public openMiniSideBarCart = () => {
    this.isSideBarOpened = this.totalQuantity > 0;
    if (this.isSideBarOpened) {
      ReaffiliatesViewMiniCart();
    }
  };
  public closeMiniSideBarCart = () => {
    this.isSideBarOpened = false;
  };
  public closeStorAssemblyPureMessage = () => {
    this.showStorAssemblyPureMessage = false;
  };
  public closeStorAssemblyMixedMessage = () => {
    this.showStorAssemblyMixedMessage = false;
  };
  get showStorAssemblyOfferMessage(): boolean {
    return this.showStorAssemblyPureMessage || this.showStorAssemblyMixedMessage;
  }
  public closeStorAssemblyOfferMessage = () => {
    this.showStorAssemblyPureMessage = false;
    this.showStorAssemblyMixedMessage = false;
  };
  public closeNewTaxRateMessage = () => {
    this.showNewTaxRateMessage = false;
  };
  public get isLoading() {
    const loadingStatuses: Array<boolean> = [this.isFetchingInProgress];
    this.products.forEach(product => {
      loadingStatuses.push(product.isProductLoading);
    });
    return loadingStatuses.some(status => status);
  }
  private isValidCartId = (cartId: string | null | undefined): cartId is string => !!cartId && cartId !== 'undefined' && validate(cartId);
  private saveCartInLocalStorage = () => {
    if (typeof window === 'undefined' || !browserStorage) return;
    const region = getRegion();
    if (this.isValidCartId(this.cartId)) {
      browserStorage.setItem(`cartId_${region}`, this.cartId);
    } else {
      browserStorage.removeItem(`cartId_${region}`);
    }
  };
  public getCartFromLocalStorage = (): boolean => {
    if (typeof window === 'undefined') return false;
    const region = getRegion();
    const cartId = browserStorage ? browserStorage.getItem(`cartId_${region}`) : null;
    if (this.isValidCartId(cartId)) {
      runInAction(() => {
        this.cartId = cartId;
      });
      return true;
    }
    if (cartId && browserStorage) {
      browserStorage.removeItem(`cartId_${region}`);
    }
    return false;
  };
  public get unavailableProductsQuantityToConfirm() {
    const unavailableProducts = Array.from(this.products.values()).filter((product: CartProductState) => {
      const exceedsAvailableQuantity = product.availability.amount > 0 && product.quantity > product.availability.amount;
      const confirmedAttempt = this.confirmedUnavailableQuantityProducts.has(product.position);
      if (confirmedAttempt) return true;
      return exceedsAvailableQuantity && !this.dismissedMaxQuantityPositions.has(product.position);
    });
    return unavailableProducts.filter((value, index, currentValue) => currentValue.findIndex(product => product.position === value.position) === index);
  }
  public get unavailableProductsQuantity() {
    const unavailableProducts = Array.from(this.products.values()).filter((product: CartProductState) => product.showProductQuantityLimitedMessage || product.showProductUnavailableMessage);
    if (unavailableProducts.length > 0) {
      runInAction(() => {
        this.showProductsUnavailableMessage = true;
      });
    }
    return unavailableProducts;
  }
  public confirmUnavailableQuantity = action((position: string | null, deleteProduct = false) => {
    if (position && !deleteProduct) {
      this.dismissedMaxQuantityPositions.delete(position);
      this.confirmedUnavailableQuantityProducts.add(position);
      this.maxQuantityBlockedWithoutProduct = false;
      this.showProductMaxQuantityMessage = true;
    } else if (position && deleteProduct) {
      this.confirmedUnavailableQuantityProducts.delete(position);
      if (this.confirmedUnavailableQuantityProducts.size === 0 && this.unavailableProductsQuantityToConfirm.length === 0) {
        this.showProductMaxQuantityMessage = false;
      }
    }
  });
  public notifyMaxQuantityReached = (productId?: string) => {
    const product = productId ? Array.from(this.products.values()).find(item => item.productId.toString() === productId) : undefined;
    if (product) {
      this.confirmUnavailableQuantity(product.position);
    } else {
      this.maxQuantityBlockedWithoutProduct = true;
      this.showProductMaxQuantityMessage = true;
    }
    this.isSideBarOpened = true;
  };
  public get productsOutOfStock() {
    const productsTemporaryUnavailable = Array.from(this.products.values()).filter((product: CartProductState) => product.availability.status === AvailabilityType.available && product.availability.amount === 0);
    if (productsTemporaryUnavailable.length > 0) {
      runInAction(() => {
        this.showProductsAvailableWithDelayMessage = true;
      });
    }
    return productsTemporaryUnavailable;
  }
  public closeProductsAvailableWithDelayMessage = () => {
    this.showProductsAvailableWithDelayMessage = false;
  };
  public closeProductsUnavailableMessage = () => {
    this.showProductsUnavailableMessage = false;
  };
  public closeProductMaxQuantityMessage = () => {
    this.unavailableProductsQuantityToConfirm.forEach(product => {
      this.dismissedMaxQuantityPositions.add(product.position);
    });
    this.confirmedUnavailableQuantityProducts.clear();
    this.maxQuantityBlockedWithoutProduct = false;
    this.showProductMaxQuantityMessage = false;
  };
  public setProperty = (key: string, value: string | boolean) => {
    this[key] = value;
  };
  public clearStore = () => {
    this.resetStore();
    this.showProductMaxQuantityMessage = false;
    this.maxQuantityBlockedWithoutProduct = false;
    this.confirmedUnavailableQuantityProducts.clear();
    this.dismissedMaxQuantityPositions.clear();
    this.saveCartInLocalStorage();
  };
  public setDiscountInputOpened = (value: boolean) => {
    this.discountInputOpened = value;
  };
  public getDiscountValue = (): string | false => {
    const discountAmount = this.totals.discounts?.amount ?? 0;
    if (!this.coupon || discountAmount === 0) return false;
    return formatPrice(discountAmount, this.totals.discounts?.currency);
  };
  public setErrorDiscountValidate = (value: boolean): void => {
    this.errorDiscountValidate = value;
  };
  public setDiscountCouponActive = (value: boolean): void => {
    this.discountCouponActive = value;
  };
  private setDiscountData = (data, discountCode): void => {
    this.discountData = {
      discountCode,
      validationStatus: data.success,
      message: data.message,
      discountValue: data.success && this.getDiscountValue(),
      coupon: this.coupon
    };
  };
  public processDiscountCode = async (discountCode: string | false = false): Promise<void> => {
    let data: JSON = {} as JSON;
    this.spinnerLoader = true;
    this.cuponReachedMaxUsage = false;
    try {
      data = discountCode ? await NextCheckoutApi.checkDiscountCode(this.cartId, discountCode) : await NextCheckoutApi.removeDiscountCode(this.cartId);
      runInAction(() => {
        void this.fetchAndPopulateCartStore(false);
        this.setDiscountData(data, discountCode);
        this.spinnerLoader = false;
      });
    } catch (e) {
      logger.error(e);
      throw e instanceof Error ? e : new Error(String(e));
    }
  };
  public setIsCartOutdated = (value: boolean): void => {
    this.isCartOutdated = value;
  };
  public setCuponReachedMaxUsage = (value: boolean): void => {
    this.cuponReachedMaxUsage = value;
  };
  public filterOnlyAssembableProducts = (productsToFilter: CartProductState[]) => productsToFilter.filter(product => product.isAssemblable);
  private handleDeliveryTime = () => {
    const newDeliveryDays: number[] = [];
    const estimated = this.estimatedDelivery;
    if (Object.values(estimated).length) {
      Object.values(estimated).forEach(d => newDeliveryDays.push(d.days));
      if (newDeliveryDays?.length && this.deliveryDays !== null) {
        newDeliveryDays.forEach((day, index) => {
          if (this.deliveryDays && !this.deliveryDays[index] || this.deliveryDays && this.deliveryDays[index] !== day) {
            runInAction(() => {
              this.showChangedDeliveryTimeMessage = true;
            });
          }
        });
      }
    }
    if (newDeliveryDays.length) {
      this.deliveryDays = newDeliveryDays;
    } else {
      this.deliveryDays = null;
      runInAction(() => {
        this.showChangedDeliveryTimeMessage = false;
      });
    }
  };
  public closeChangedDeliveryTimeMessage = () => {
    this.showChangedDeliveryTimeMessage = false;
  };
  public get minPriceDelivery(): number {
    let pricesArray: Record<string, number[]> = {};
    this.transports.forEach(transport => {
      pricesArray = {
        ...pricesArray,
        [transport.transportGroupId]: []
      };
    });
    if (!this.availableDeliveries.size) {
      let price = 0;
      this.transports.forEach(transport => {
        price += transport.totalPrice?.amount || 0;
      });
      return price;
    }
    this.transports.forEach(transport => {
      this.availableDeliveries.get(transport.transportGroupId)?.forEach(delivery => {
        if (typeof delivery.price?.amount === 'number') {
          pricesArray[transport.transportGroupId].push(delivery.price.amount);
        }
      });
    });
    let minPrice = 0;
    this.transports.forEach(transport => {
      minPrice += Math.min(...pricesArray[transport.transportGroupId]);
    });
    return minPrice;
  }
  public refreshCart = async () => {
    const data = await this.handleCartStatus();
    const isOfCartType = (object: ICart | IApiError): object is ICart => 'cartId' in object;
    runInAction(() => {
      if (isOfCartType(data)) {
        const cartData = data;
        this.cartStatus = cartData.status;
        Object.keys(cartData).forEach(key => {
          if (!cartData[key]) return;
          if (typeof cartData[key] === 'string') this[key] = cartData[key];
          if (key === 'coupon') {
            this.coupon = isObject(cartData[key]) ? cartData[key] as ICoupon : null;
          }
          if (!cartData[key].length) return;
          if (key === 'positions') {
            const grouppedByType = groupBy(cartData[key], pos => pos.type);
            Object.keys(grouppedByType).forEach(posTypeKey => {
              this.positionsFactoryDispatcher(posTypeKey as TPositionType, grouppedByType);
            });
          }
          if (key === 'totals') {
            cartData[key].forEach(el => {
              this.totals[el.totalType] = el.total;
            });
          }
          if (key === 'transports') {
            const newTransports = cartData[key].map(el => ({
              ...el,
              totalPrice: el.totalPrice
            }));
            this.transports = newTransports;
          }
        });
        this.checkSelectedDeliveries();
        this.checkSelectedServices();
        this.checkStorAssembly();
      }
    });
  };
  public addProductsToCart = async (productArray: ICartData[]) => {
    if (!this.cartId) {
      await this.createCart();
    }
    let addPositionsResponse = await NextCheckoutApi.addPositions(this.cartId, productArray);
    if ('errorsData' in addPositionsResponse) {
      const {
        errorsData
      } = addPositionsResponse;
      const errorObject = Array.isArray(errorsData) ? errorsData[0] : {};
      if ('error' in errorObject && 'message' in errorObject && 'status' in errorObject && errorObject.status === 409 && errorObject.message === CANNOT_MODIFY_CART) {
        await this.invalidateCart();
        await this.createCart();
        addPositionsResponse = await NextCheckoutApi.addPositions(this.cartId, productArray);
      }
    }
    return addPositionsResponse;
  };
  public setAvailableServicesPool = (transportGroupId: string, servicesPool: IAvailableServiceV2[]) => {
    this.availableServicesPool[transportGroupId] = servicesPool;
  };
  private getCartProductPriceInputs = (useBasePrices = false): CartProductPriceInput[] => {
    const priceKind = useBasePrices ? 'base' : 'current';
    return Array.from(this.products.values()).map(product => ({
      positionId: product.position,
      assemblyAmount: product.prices.total[priceKind].assembly.amount,
      grossAmount: product.prices.total[priceKind].gross.amount,
      productTaxRate: product.prices.taxRate,
      isStorProduct: product.categories.some(category => category.categoryId === STOR_CATEGORY_ID)
    }));
  };
  public getAssemblyProductsPrice = (transportGroupId: string, assemblyServiceId: string = ASSEMBLY_SERVICE_VAT_8_ID): number => {
    const servicesPool = this.availableServicesPool[transportGroupId];
    const products = this.getCartProductPriceInputs();
    if (servicesPool?.length && products.length) {
      const fromCartProducts = calculateAssemblyProductsPriceFromServices(servicesPool, products, assemblyServiceId, {
        isMixedCart: this.showStorAssemblyMixedMessage
      });
      if (fromCartProducts > 0) {
        return fromCartProducts;
      }
    }
    if (this.showStorAssemblyMixedMessage && products.length) {
      return calculateMixedAssemblyProductsPriceFromCart(products);
    }
    return this.totalProductsTotalCurrentAssemblyAmount;
  };
  public getSelfAssemblyProductsPrice = (_transportGroupId: string): number => {
    const products = this.getCartProductPriceInputs();
    if (products.length) {
      return calculateSelfAssemblyProductsPrice(products);
    }
    return this.totalProductsTotalCurrentGrossAmount;
  };
  public isMixedAssemblyVat = (transportGroupId: string, assemblyServiceId: string = ASSEMBLY_SERVICE_VAT_8_ID): boolean => {
    if (this.showStorAssemblyMixedMessage) {
      return true;
    }
    const servicesPool = this.availableServicesPool[transportGroupId];
    if (!servicesPool?.length) {
      return false;
    }
    return isMixedAssemblyTaxRate(servicesPool, assemblyServiceId);
  };
  get totalProductsTotalCurrentGrossAmount() {
    return Array.from(this.products.values()).reduce((acc, product) => acc + product.prices.total.current.gross.amount, 0);
  }
  get totalProductsTotalCurrentAssemblyAmount() {
    return Array.from(this.products.values()).reduce((acc, product) => acc + product.prices.total.current.assembly.amount, 0);
  }
  private getStorAssemblyCartType = (): 'pure' | 'mixed' | null => {
    const region = getRegion('uppercase') as TRegion;
    if (region !== AVAILABLE_REGIONS.poland || Object.keys(this.products).length === 0) {
      return null;
    }
    const productsArray = Array.from(this.products.values());
    const hasStoreProducts = productsArray.some(product => product.productType?.id === CLOSET_TYPE_ID && product.productSubtype?.id === CLOSET_SUBTYPE_ID && product.categories.some(category => category.categoryId === STOR_CATEGORY_ID));
    const hasSecondTypeProducts = productsArray.some(product => product.productType?.id === CLOSET_TYPE_ID && product.productSubtype?.id === CLOSET_SUBTYPE_MASKING_ID);
    const hasOtherProducts = productsArray.some(product => !product.isDip && !product.categories.some(category => category.categoryId === STOR_CATEGORY_ID));
    if (!hasStoreProducts || !hasSecondTypeProducts) {
      return null;
    }
    return hasOtherProducts ? 'mixed' : 'pure';
  };
  get isStorAssemblyPureCart(): boolean {
    return this.getStorAssemblyCartType() === 'pure';
  }
  public checkStorAssembly = () => {
    const productsArray = Array.from(this.products.values());
    this.showStorAssemblyPureMessage = false;
    this.showStorAssemblyMixedMessage = false;
    if (productsArray.length === 0) return;
    const storAssemblyCartType = this.getStorAssemblyCartType();
    if (storAssemblyCartType === 'pure') {
      runInAction(() => {
        this.showStorAssemblyPureMessage = true;
      });
    } else if (storAssemblyCartType === 'mixed') {
      runInAction(() => {
        this.showStorAssemblyMixedMessage = true;
      });
    } else {
      return;
    }

    // check if any of selected assembly has tax rate 8
    this.showNewTaxRateMessage = false;
    let hasNewTaxRate = false;
    this.transports.forEach(transport => {
      if (Object.keys(this.selectedAssemblyService).length > 0 && this.selectedAssemblyService[transport.transportGroupId] && this.selectedAssemblyService[transport.transportGroupId]?.prices.taxRate.toString() === '8') {
        hasNewTaxRate = true;
      }
    });
    if (hasNewTaxRate && this.currentAssemblyTaxRate !== '8' || !hasNewTaxRate && this.currentAssemblyTaxRate === '8') {
      this.showNewTaxRateMessage = true;
      this.currentAssemblyTaxRate = hasNewTaxRate ? '8' : '23';
    }
  };
  get isAssembly8VatSelected(): boolean {
    return this.services.some(service => service.name === ASSEMBLY_SERVICE_NAME && (service.serviceId === ASSEMBLY_SERVICE_VAT_8_ID || service.prices.taxRate.toString() === '8'));
  }
  private hasMixedProductTypesInCart = (): boolean => {
    const products = Array.from(this.products.values());
    if (products.length < 2) {
      return false;
    }
    const hasStorProduct = products.some(product => product.categories.some(category => category.categoryId === STOR_CATEGORY_ID));
    const hasNonStorProduct = products.some(product => !product.categories.some(category => category.categoryId === STOR_CATEGORY_ID));
    return hasStorProduct && hasNonStorProduct;
  };
  get isMixedAssemblySummary(): boolean {
    if (!this.isAssembly8VatSelected) {
      return false;
    }
    return this.hasMixedProductTypesInCart() || this.showStorAssemblyMixedMessage || this.transports.some(transport => this.isMixedAssemblyVat(transport.transportGroupId));
  }
  private getMixedAssemblyTransportGroupId = (): string | undefined => {
    const transportWithAssembly = this.transports.find(transport => this.selectedAssemblyService[transport.transportGroupId]);
    return transportWithAssembly?.transportGroupId ?? this.transports[0]?.transportGroupId;
  };
  public getMixedAssemblyDisplayPrice = (transportGroupId: string): number | null => {
    const servicesPool = this.availableServicesPool[transportGroupId] ?? [];
    const products = this.getCartProductPriceInputs();
    if (!products.length || !servicesPool.length) {
      return null;
    }
    const isMixedCart = this.showStorAssemblyMixedMessage || this.hasMixedProductTypesInCart() || this.isMixedAssemblyVat(transportGroupId);
    return calculateMixedAssemblyDisplayPrice(products, servicesPool, {
      isMixedCart
    });
  };
  public getMixedEligibleProductsPriceComparison = (transportGroupId: string): {
    productsAt23BeforeAssembly: number;
    productsAt8: number;
  } | null => {
    const servicesPool = this.availableServicesPool[transportGroupId] ?? [];
    const products = this.getCartProductPriceInputs();
    if (!products.length || !servicesPool.length) {
      return null;
    }
    const isMixedCart = this.showStorAssemblyMixedMessage || this.hasMixedProductTypesInCart() || this.isMixedAssemblyVat(transportGroupId);
    return calculateMixedEligibleProductsPriceComparison(products, servicesPool, ASSEMBLY_SERVICE_VAT_8_ID, {
      isMixedCart
    });
  };
  public getMixedAssemblySavings = (): {
    amount: number;
    currency: string;
  } | null => {
    const breakdown = this.getMixedVatBreakdown(false);
    if (!breakdown) {
      return null;
    }
    const transportGroupId = this.getMixedAssemblyTransportGroupId();
    if (!transportGroupId) {
      return null;
    }
    const servicesPool = this.availableServicesPool[transportGroupId] ?? [];
    const products = this.getCartProductPriceInputs();
    const isMixedCart = this.showStorAssemblyMixedMessage || this.hasMixedProductTypesInCart() || this.isMixedAssemblyVat(transportGroupId);
    const bringFromCart = Object.values(this.selectedBringingService).reduce((sum, service) => sum + service.prices.total.current.gross.amount, 0);
    const amount = calculateMixedAssemblySavings(products, servicesPool, {
      isMixedCart
    }, {
      products8: breakdown.products8,
      products23: breakdown.products23,
      assembly8: breakdown.assembly8,
      assembly23: breakdown.assembly23,
      bring: bringFromCart > 0 ? bringFromCart : getBringServiceGrossPriceFromPool(servicesPool, true)
    });
    if (amount == null || amount <= 0) {
      return null;
    }
    return {
      amount,
      currency: breakdown.currency
    };
  };
  private getMixedVatBreakdown = (useBasePrices: boolean): (MixedVatProductsBreakdown & MixedVatAssemblyBreakdown & {
    currency: string;
  }) | null => {
    if (!this.isMixedAssemblySummary) {
      return null;
    }
    const assemblyService = this.services.find(service => service.name === ASSEMBLY_SERVICE_NAME);
    // Prefer taxesTotals from API; client calc only when API split is incomplete/unavailable.
    const breakdownFromApi = getMixedVatBreakdownFromTaxesTotals(this.taxesTotals, this.taxes, useBasePrices, assemblyService?.prices.components);
    if (breakdownFromApi) {
      return {
        ...breakdownFromApi,
        currency: this.totals.products?.currency ?? 'PLN'
      };
    }
    const transportGroupId = this.getMixedAssemblyTransportGroupId();
    if (!transportGroupId) {
      return null;
    }
    const priceKind = useBasePrices ? 'base' : 'current';
    const servicesPool = this.availableServicesPool[transportGroupId] ?? [];
    const products = this.getCartProductPriceInputs(useBasePrices);
    const isMixedCart = this.showStorAssemblyMixedMessage || this.hasMixedProductTypesInCart() || this.isMixedAssemblyVat(transportGroupId);
    const {
      products8,
      products23
    } = calculateMixedVatProductsBreakdown(products, servicesPool, ASSEMBLY_SERVICE_VAT_8_ID, {
      isMixedCart
    });
    let {
      assembly8,
      assembly23
    } = calculateMixedVatAssemblyBreakdown(servicesPool, ASSEMBLY_SERVICE_VAT_8_ID);
    if (assembly8 === 0 && assembly23 === 0 && assemblyService?.prices.components?.length) {
      assembly8 = assemblyService.prices.components.filter(component => String(component.gross.taxRate) === '8').reduce((sum, component) => sum + component.gross.amount, 0);
      assembly23 = assemblyService.prices.components.filter(component => String(component.gross.taxRate) === '23').reduce((sum, component) => sum + component.gross.amount, 0);
    }
    if (assembly8 === 0 && assembly23 === 0) {
      const totalAssembly = this.services.filter(service => service.name === ASSEMBLY_SERVICE_NAME).reduce((acc, service) => acc + service.prices.total[priceKind].gross.amount, 0);
      if (totalAssembly > 0) {
        const productsTotal = products8 + products23;
        if (products8 > 0 && products23 > 0 && productsTotal > 0) {
          assembly8 = Math.round(totalAssembly * products8 / productsTotal);
          assembly23 = totalAssembly - assembly8;
        } else if (products8 > 0) {
          assembly8 = totalAssembly;
        } else {
          assembly23 = totalAssembly;
        }
      }
    }
    const hasReducedVat = products8 > 0 || assembly8 > 0;
    const hasStandardVat = products23 > 0 || assembly23 > 0;
    if (!hasReducedVat || !hasStandardVat) {
      return null;
    }
    return {
      products8,
      products23,
      assembly8,
      assembly23,
      currency: this.totals.products?.currency ?? 'PLN'
    };
  };
  public getMixedVatSummaryBreakdown = (): (MixedVatProductsBreakdown & MixedVatAssemblyBreakdown & {
    currency: string;
  }) | null => this.getMixedVatBreakdown(true);
  get isPromoConsentRequired(): boolean {
    return this.coupon?.code?.toLowerCase() === PROMO_CONSENT_COUPON_CODE.toLowerCase();
  }
  public setCustomerId = (customerId: string) => {
    this.customerId = customerId;
  };
  public setAlertReducedQuantity = (objectUuid: string, configuratorId: string) => {
    this.products.forEach(product => {
      if (product.productId.toString() === objectUuid || product.productId.toString() === configuratorId) {
        product.alertReducedQuantity = true;
      }
    });
  };
}